<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Nouveau Lot</h1>
        <p class="page-subtitle" v-if="selectedProducts.length > 0">{{ selectedProducts.length }} produit{{ selectedProducts.length > 1 ? 's' : '' }} — Total: {{ formatNumber(grandTotal) }}</p>
      </div>
      <div class="d-flex align-center header-actions" :class="{ 'header-actions--compact': isCompact }" style="gap: 8px;">
        <v-select
          v-model="storeId"
          :items="stores"
          item-text="name"
          item-value="id"
          label="Emplacement"
          outlined
          dense
          hide-details
          style="min-width: 190px;"
          @change="onStoreChange"
        ></v-select>
        <template v-if="!isCompact">
          <v-btn outlined small @click="resetData" v-if="selectedProducts.length > 0">
            <v-icon left small>mdi-refresh</v-icon>
            Réinitialiser
          </v-btn>
          <v-btn color="primary" depressed @click="saveBatch" :disabled="selectedProducts.length === 0">
            <v-icon left small>mdi-content-save</v-icon>
            Enregistrer
          </v-btn>
        </template>
        <!-- Petit écran : Enregistrer passe dans la barre du bas, le reste dans ce menu -->
        <v-menu v-else-if="selectedProducts.length > 0" offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon large v-bind="attrs" v-on="on" aria-label="Plus d'actions">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="exportExcel">
              <v-list-item-icon><v-icon small color="success">mdi-file-excel</v-icon></v-list-item-icon>
              <v-list-item-title>Exporter Excel</v-list-item-title>
            </v-list-item>
            <v-list-item @click="resetData">
              <v-list-item-icon><v-icon small>mdi-refresh</v-icon></v-list-item-icon>
              <v-list-item-title>Vider le lot</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Add products bar -->
    <v-card flat class="filter-card mb-4">
      <v-card-text class="py-3">
        <v-row align="center" dense>
          <v-col cols="12" :sm="isCompact ? 12 : 6" md="4">
            <v-autocomplete
              v-model="selectedProduct"
              :items="allProducts"
              item-text="name"
              item-value="id"
              placeholder="Rechercher un produit..."
              outlined
              hide-details
              clearable
              @change="quickAdd"
              prepend-inner-icon="mdi-magnify"
              :filter="filterProducts"
              :dense="!isCompact"
              :class="{ 'compact-search': isCompact }"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" :sm="isCompact ? 12 : 6" md="auto">
            <div class="d-flex" :class="{ 'add-actions--compact': isCompact }" style="gap: 8px;">
              <v-btn outlined :small="!isCompact" @click="dialogCategory = true">
                <v-icon left small>mdi-folder-plus-outline</v-icon>
                Par catégorie
              </v-btn>
              <v-btn outlined :small="!isCompact" @click="dialogAllProducts = true">
                <v-icon left small>mdi-playlist-plus</v-icon>
                Tous les produits
              </v-btn>
              <v-btn outlined small color="success" @click="exportExcel" v-if="!isCompact && selectedProducts.length > 0">
                <v-icon left small>mdi-file-excel</v-icon>
                Excel
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Preload indicator -->
    <div v-if="loadingProducts" class="page-loader">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="40" width="3" class="mb-3"></v-progress-circular>
        <p class="text-body-2 text--secondary">Chargement des produits...<br>Cela peut prendre quelques secondes, patientez.</p>
      </div>
    </div>

    <!-- Petit écran (< 1024 px) : une carte par produit, champs tactiles -->
    <div v-if="selectedProducts.length > 0 && isCompact">
      <div class="batch-cards">
        <div v-for="(item, idx) in selectedProducts" :key="item.id" class="batch-card">
          <div class="batch-card-head">
            <div class="batch-card-name">
              <div class="font-weight-bold">{{ item.name }}</div>
              <div class="text-caption text--secondary">{{ item.barcode || '—' }} · {{ getCategoryName(item.category_id) }}</div>
            </div>
            <v-btn text small color="error" class="batch-card-remove" @click="deleteProduct(idx)">Retirer</v-btn>
          </div>
          <div class="batch-card-stock">
            Stock actuel <strong>{{ item.stock }}</strong>
            <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
            <strong :class="projectedStock(item) < 0 ? 'error--text' : 'success--text'">{{ projectedStock(item) }}</strong>
          </div>
          <div class="batch-card-fields">
            <div>
              <label class="form-label">Quantité</label>
              <div class="qty-stepper">
                <button type="button" class="qty-btn" aria-label="Diminuer" @click="stepQuantity(item, -1)">−</button>
                <input type="number" inputmode="decimal" v-model.number="item.quantity" min="0" class="touch-input" />
                <button type="button" class="qty-btn" aria-label="Augmenter" @click="stepQuantity(item, 1)">+</button>
              </div>
            </div>
            <div>
              <label class="form-label">Coût unitaire</label>
              <input type="number" inputmode="decimal" v-model.number="item.cost" min="0" step="0.01" class="touch-input" />
            </div>
          </div>
          <div class="batch-card-total">
            Total ligne : <strong>{{ formatNumber(item.quantity * item.cost) }}</strong>
          </div>
        </div>
      </div>

      <!-- Barre du bas, visible pendant le défilement -->
      <div class="batch-bottom-bar">
        <div class="batch-bottom-summary">
          <div class="text-caption text--secondary">{{ selectedProducts.length }} produit{{ selectedProducts.length > 1 ? 's' : '' }} · {{ totalQuantity }} unités</div>
          <div class="batch-bottom-total">{{ formatNumber(grandTotal) }}</div>
        </div>
        <v-btn color="primary" depressed large @click="saveBatch">
          <v-icon left small>mdi-content-save</v-icon>
          Enregistrer
        </v-btn>
      </div>
    </div>

    <!-- Products spreadsheet -->
    <div v-else-if="selectedProducts.length > 0" class="spreadsheet-card">
      <div class="spreadsheet-wrapper">
        <table class="spreadsheet">
          <thead>
            <tr>
              <th style="width: 36px;">#</th>
              <th class="text-left" style="width: 26%;">PRODUIT</th>
              <th v-if="!$vuetify.breakpoint.xsOnly" class="text-right" style="width: 10%;">PRIX VENTE</th>
              <th v-if="!$vuetify.breakpoint.xsOnly" class="text-center" style="width: 9%;">STOCK ACTUEL</th>
              <th class="text-center" style="width: 10%;">QUANTITÉ</th>
              <th v-if="!$vuetify.breakpoint.xsOnly" class="text-center" style="width: 9%;">STOCK PROJETÉ</th>
              <th class="text-right" style="width: 11%;">COÛT UNIT.</th>
              <th class="text-right" style="width: 11%;">TOTAL</th>
              <th style="width: 44px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in selectedProducts" :key="item.id">
              <td class="text-center text-caption text--secondary">{{ idx + 1 }}</td>
              <td class="text-left">
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption text--secondary">{{ item.barcode || '—' }} · {{ getCategoryName(item.category_id) }}</div>
              </td>
              <td v-if="!$vuetify.breakpoint.xsOnly" class="text-right text-caption">{{ formatNumber(item.price) }}</td>
              <td v-if="!$vuetify.breakpoint.xsOnly" class="text-center text-caption">{{ item.stock }}</td>
              <td class="text-center">
                <input
                  type="number"
                  v-model.number="item.quantity"
                  min="0"
                  class="cell-input cell-number"
                />
              </td>
              <td v-if="!$vuetify.breakpoint.xsOnly" class="text-center font-weight-medium" :class="projectedStock(item) < 0 ? 'error--text' : 'success--text'">
                {{ projectedStock(item) }}
              </td>
              <td class="text-right">
                <input
                  type="number"
                  v-model.number="item.cost"
                  min="0"
                  step="0.01"
                  class="cell-input cell-number"
                />
              </td>
              <td class="text-right font-weight-bold">{{ formatNumber(item.quantity * item.cost) }}</td>
              <td class="text-center">
                <v-btn icon x-small color="error" @click="deleteProduct(idx)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="totals-row">
              <td :colspan="$vuetify.breakpoint.xsOnly ? 2 : 4" class="text-left font-weight-bold">TOTAL</td>
              <td class="text-center font-weight-bold">{{ totalQuantity }}</td>
              <td :colspan="$vuetify.breakpoint.xsOnly ? 1 : 2"></td>
              <td class="text-right font-weight-bold">{{ formatNumber(grandTotal) }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Empty state -->
    <v-card flat v-else-if="!loadingProducts" class="empty-card">
      <v-card-text class="text-center py-12">
        <v-icon size="56" color="grey lighten-2">mdi-package-variant-plus</v-icon>
        <h3 class="text-body-1 font-weight-medium mt-4 mb-1">Aucun produit dans le lot</h3>
        <p class="text-caption text--secondary">Utilisez la barre ci-dessus pour ajouter des produits</p>
      </v-card-text>
    </v-card>

    <!-- Danger zone: stock reset (admin only) — en bas de page -->
    <div v-if="isAdmin" class="danger-zone mt-8 mb-4">
      <div class="danger-zone-title">Zone dangereuse</div>
      <v-btn outlined color="error" @click="$refs.resetDialog.open()">
        <v-icon left small>mdi-restore-alert</v-icon> Réinitialiser le stock
      </v-btn>
      <stock-reset-dialog
        ref="resetDialog"
        :products="allProducts"
        :categories="categories"
        :store-id="storeId"
        @done="onResetDone"
        @error="onResetError"
      />
    </div>

    <!-- Category dialog -->
    <v-dialog v-model="dialogCategory" max-width="440" @click:outside="dialogCategory = false">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-folder-plus-outline</v-icon>
          Ajouter par catégorie
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <div class="form-group">
            <label class="form-label">Catégorie</label>
            <v-select v-model="categorySelected" :items="categories" item-text="name" item-value="id" outlined dense hide-details clearable placeholder="Sélectionner"></v-select>
          </div>
          <div class="form-group">
            <label class="form-label">Quantité pour tous</label>
            <v-text-field v-model.number="categoryQuantity" type="number" min="0" outlined dense hide-details placeholder="0 = à modifier après"></v-text-field>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogCategory = false">Annuler</v-btn>
          <v-btn color="primary" depressed :disabled="!categorySelected" :loading="addingCategory" @click="addCategoryProducts">Ajouter</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- All products dialog -->
    <v-dialog v-model="dialogAllProducts" max-width="440" @click:outside="dialogAllProducts = false">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-playlist-plus</v-icon>
          Ajouter tous les produits
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <div class="form-group">
            <label class="form-label">Quantité pour tous</label>
            <v-text-field v-model.number="allProductsQuantity" type="number" min="0" outlined dense hide-details placeholder="0 = à modifier après"></v-text-field>
          </div>
          <p class="text-caption text--secondary mt-1">{{ allProducts.length }} produits seront ajoutés au lot.</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogAllProducts = false">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="addingAll" @click="addAllProducts">Ajouter</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Save dialog -->
    <v-dialog v-model="saveDialog" max-width="480" @click:outside="saveDialog = false">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-content-save</v-icon>
          Enregistrer le lot
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <v-alert type="info" text dense class="mb-4">
            <strong>{{ selectedProducts.length }}</strong> produit{{ selectedProducts.length > 1 ? 's' : '' }} — Total: <strong>{{ formatNumber(grandTotal) }}</strong>
          </v-alert>
          <div class="form-group">
            <div class="recap-value">Emplacement : <strong>{{ selectedStoreName }}</strong></div>
          </div>
          <div class="form-group">
            <label class="form-label">Type de lot *</label>
            <v-select v-model="batchType" :items="batchTypes" item-text="text" item-value="value" outlined dense hide-details placeholder="Sélectionner"></v-select>
          </div>
          <div class="form-group">
            <label class="form-label">Mémo *</label>
            <v-textarea v-model="memo" outlined dense rows="3" hide-details placeholder="Commentaire sur ce lot..."></v-textarea>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="saveDialog = false">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="submitLoading" :disabled="!batchType || !memo" @click="handleSave">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FlashMessage :position="'right bottom'"></FlashMessage>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import StockResetDialog from '@/components/StockResetDialog.vue'

export default {
  name: 'NewBatchView',
  components: { BreadcrumbComponent, StockResetDialog },
  data() {
    return {
      items: [
        { text: 'Inventaire', disabled: false, href: '/batches' },
        { text: 'Nouveau Lot', disabled: true, href: '#' }
      ],
      batchTypes: [
        { text: 'Réception', value: 1 },
        { text: 'Ajustement', value: 2 }
      ],
      // Preloaded data
      allProducts: [],
      categories: [],
      loadingProducts: true,
      // Store selector (page-level, single source of truth: stock affiché, lot enregistré, reset)
      stores: [],
      storeId: null,
      // Selection
      selectedProduct: null,
      selectedProducts: [],
      // Dialogs
      dialogCategory: false,
      dialogAllProducts: false,
      saveDialog: false,
      // Category dialog
      categorySelected: null,
      categoryQuantity: 0,
      addingCategory: false,
      // All products dialog
      allProductsQuantity: 0,
      addingAll: false,
      // Save dialog
      batchType: null,
      memo: null,
      submitLoading: false,
    }
  },

  computed: {
    // Sous ~1024 px (téléphone, tablette), le tableau à 7+ colonnes rend les champs
    // inutilisables : on passe aux cartes.
    isCompact() {
      return this.$vuetify.breakpoint.width < 1024
    },
    grandTotal() {
      return this.selectedProducts.reduce(function (sum, p) { return sum + (p.quantity * p.cost) }, 0)
    },
    totalQuantity() {
      return this.selectedProducts.reduce(function (sum, p) { return sum + (parseInt(p.quantity) || 0) }, 0)
    },
    isAdmin() {
      const u = this.$store.getters.StateUser || {}
      return Number(u.role_id) === 1 || (u.role && Number(u.role.id) === 1)
    },
    selectedStoreName() {
      var found = this.stores.find(function (s) { return s.id === this.storeId }, this)
      return found ? found.name : '—'
    },
  },

  methods: {
    formatNumber(value) {
      if (!value && value !== 0) return '0.00'
      var parts = Math.abs(parseFloat(value)).toFixed(2).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return parts.join('.')
    },

    stepQuantity(item, delta) {
      var q = (parseFloat(item.quantity) || 0) + delta
      item.quantity = q < 0 ? 0 : q
    },

    projectedStock(item) {
      return (parseFloat(item.stock) || 0) + (parseFloat(item.quantity) || 0)
    },

    filterProducts(item, queryText) {
      var q = (queryText || '').toLowerCase()
      return (item.name && item.name.toLowerCase().indexOf(q) !== -1) ||
             (item.barcode && item.barcode.toLowerCase().indexOf(q) !== -1)
    },

    getCategoryName(catId) {
      var cat = this.categories.find(function (c) { return c.id === catId })
      return cat ? cat.name : '—'
    },

    // Magasin(s) du business — sélecteur page-level : source unique pour le stock
    // affiché, le lot enregistré et le dialogue de reset (préselection).
    async loadStores() {
      try {
        var businessId = JSON.parse(localStorage.business).id
        var res = await this.$axios.get('/stores?orderasc=name&business_id=' + businessId)
        this.stores = res.data || []
        if (!this.storeId && this.stores.length) this.storeId = this.stores[0].id
      } catch (e) {
        console.error('Stores error:', e)
        this.stores = []
      }
    },

    // Recharge le stock du ledger pour le magasin sélectionné, sans perdre les
    // lignes déjà ajoutées au lot : met à jour allProducts ET selectedProducts.
    async refreshStockLevels() {
      if (!this.storeId) return
      try {
        var levels = await this.$axios.get('/stock-levels?store_id=' + this.storeId)
        var byProduct = {}
        var rows = (levels.data && levels.data.data) || []
        rows.forEach(function (row) { byProduct[row.product_id] = Number(row.stock) })
        this.allProducts.forEach(function (p) {
          p.stock = byProduct[p.id] !== undefined ? byProduct[p.id] : 0
        })
        this.selectedProducts.forEach(function (p) {
          p.stock = byProduct[p.id] !== undefined ? byProduct[p.id] : 0
        })
      } catch (e) {
        console.error('Stock levels error:', e)
        this.flashMessage.info({
          title: 'Info',
          message: 'Stock du ledger indisponible : le stock affiché peut être obsolète.'
        })
      }
    },

    onStoreChange() {
      this.refreshStockLevels()
    },

    // Preload all products and categories on mount
    async preloadData() {
      this.loadingProducts = true
      try {
        var results = await Promise.all([
          this.$axios.get('/products?include=rate&orderasc=name'),
          this.$axios.get('/categories?orderasc=name'),
          this.loadStores()
        ])
        this.allProducts = results[0].data || []
        this.categories = results[1].data || []

        // Le stock affiché vient du ledger `stocks` (source unique de vérité, alimentée
        // par le cron de consolidation et lue par la réinitialisation), pas de la colonne
        // dénormalisée products.stock qui peut avoir dérivé.
        await this.refreshStockLevels()
      } catch (e) {
        console.error('Preload error:', e)
      }
      this.loadingProducts = false
    },

    // Quick add from search
    quickAdd(productId) {
      if (!productId) return
      var exists = this.selectedProducts.find(function (p) { return p.id === productId })
      if (exists) {
        this.flashMessage.info({ title: 'Info', message: 'Produit déjà dans le lot' })
        this.selectedProduct = null
        return
      }
      var product = this.allProducts.find(function (p) { return p.id === productId })
      if (product) {
        this.selectedProducts.push({
          id: product.id,
          name: product.name,
          barcode: product.barcode,
          category_id: product.category_id,
          price: product.price,
          rate_id: product.rate_id,
          rate: product.rate,
          stock: parseFloat(product.stock) || 0,
          quantity: 1,
          cost: parseFloat(product.cost) || 0,
        })
      }
      this.selectedProduct = null
    },

    addCategoryProducts() {
      if (!this.categorySelected) return
      this.addingCategory = true
      var self = this
      var qty = typeof this.categoryQuantity === 'number' && this.categoryQuantity >= 0 ? this.categoryQuantity : 0
      var existingIds = this.selectedProducts.map(function (p) { return p.id })
      var added = 0

      this.allProducts.forEach(function (p) {
        if (p.category_id === self.categorySelected && !existingIds.includes(p.id)) {
          self.selectedProducts.push({
            id: p.id, name: p.name, barcode: p.barcode, category_id: p.category_id,
            price: p.price, rate_id: p.rate_id, rate: p.rate,
            stock: parseFloat(p.stock) || 0,
            quantity: qty, cost: parseFloat(p.cost) || 0,
          })
          existingIds.push(p.id)
          added++
        }
      })

      this.dialogCategory = false
      this.addingCategory = false
      if (added > 0) this.flashMessage.success({ title: 'Succès', message: added + ' produit(s) ajouté(s)' })
      else this.flashMessage.info({ title: 'Info', message: 'Aucun nouveau produit à ajouter' })
    },

    addAllProducts() {
      this.addingAll = true
      var self = this
      var qty = typeof this.allProductsQuantity === 'number' && this.allProductsQuantity >= 0 ? this.allProductsQuantity : 0
      var existingIds = this.selectedProducts.map(function (p) { return p.id })
      var added = 0

      this.allProducts.forEach(function (p) {
        if (!existingIds.includes(p.id)) {
          self.selectedProducts.push({
            id: p.id, name: p.name, barcode: p.barcode, category_id: p.category_id,
            price: p.price, rate_id: p.rate_id, rate: p.rate,
            stock: parseFloat(p.stock) || 0,
            quantity: qty, cost: parseFloat(p.cost) || 0,
          })
          existingIds.push(p.id)
          added++
        }
      })

      this.dialogAllProducts = false
      this.addingAll = false
      if (added > 0) this.flashMessage.success({ title: 'Succès', message: added + ' produit(s) ajouté(s)' })
      else this.flashMessage.info({ title: 'Info', message: 'Tous les produits sont déjà dans le lot' })
    },

    deleteProduct(index) {
      this.selectedProducts.splice(index, 1)
    },

    onResetDone(payload) {
      this.preloadData() // recharge produits => stocks à jour à l'écran
      var msg = payload.products_reset > 0
        ? 'Lot #' + payload.batch_no + ' créé — ' + payload.products_reset + ' produit(s) remis à 0'
        : (payload.message || 'Rien à réinitialiser')
      this.flashMessage.success({ title: 'Succès', message: msg })
    },

    onResetError(message) {
      this.flashMessage.error({ title: 'Erreur', message: message })
    },

    resetData() {
      this.selectedProduct = null
      this.selectedProducts = []
      this.batchType = null
      this.memo = null
    },

    saveBatch() {
      if (this.selectedProducts.length === 0) return
      this.saveDialog = true
    },

    handleSave() {
      if (!this.batchType || !this.memo) return

      var authentication = JSON.parse(localStorage.authentication)
      var bt = this.batchType
      var batchData = {
        batch_memo: this.memo,
        solgc_user: authentication.user.id,
        solgc_schema: authentication.schema,
        store_id: this.storeId,
        inventories: this.selectedProducts.map(function (p) {
          return {
            type: bt,
            product_id: p.id,
            rate_id: p.rate_id,
            quantity: p.quantity,
            cost: p.cost,
            price: p.price,
          }
        })
      }

      var self = this
      this.submitLoading = true
      this.$axios.post('/operations/bulk-store', batchData)
        .then(function (res) {
          self.submitLoading = false
          if (res && res.data && res.data.success) {
            self.saveDialog = false
            self.flashMessage.success({ title: 'Succès', message: 'Lot enregistré avec succès' })
            self.resetData()
          } else {
            alert("Erreur lors de l'enregistrement")
          }
        })
        .catch(function () {
          self.submitLoading = false
          alert("Erreur lors de l'enregistrement")
        })
    },

    exportExcel() {
      var XLSX = require('xlsx')
      var wsData = [['Produit', 'Code barre', 'Prix vente', 'Quantité', 'Coût unit.', 'Total']]
      this.selectedProducts.forEach(function (p) {
        wsData.push([p.name, p.barcode || '', p.price, p.quantity, p.cost, p.quantity * p.cost])
      })
      wsData.push([])
      wsData.push(['TOTAL', '', '', this.totalQuantity, '', this.grandTotal])

      var wb = XLSX.utils.book_new()
      var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 14 }]
      XLSX.utils.book_append_sheet(wb, ws, 'Lot')
      XLSX.writeFile(wb, 'nouveau_lot.xlsx')
    },
  },

  mounted() {
    this.preloadData()
  },
}
</script>

<style scoped>
.filter-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}

.empty-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}

/* Spreadsheet */
.spreadsheet-card {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.spreadsheet-wrapper {
  overflow: auto;
  max-height: calc(100vh - 280px);
}

.spreadsheet {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.spreadsheet thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f1f5f9;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  padding: 10px 12px;
  border-bottom: 2px solid #cbd5e1;
  border-right: 1px solid #e2e8f0;
  white-space: nowrap;
}

.spreadsheet thead th:last-child { border-right: none; }

.spreadsheet tbody td {
  padding: 6px 12px;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  font-size: 0.8125rem;
  height: 42px;
}

.spreadsheet tbody td:last-child { border-right: none; }
.spreadsheet tbody tr:hover td { background: #f8fafc; }

.spreadsheet tfoot td {
  padding: 10px 12px;
  border-top: 2px solid #194173;
  background: #f1f5f9;
  font-size: 0.8125rem;
}

.cell-input {
  display: block;
  width: 100%;
  height: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  outline: none;
  padding: 4px 8px;
  font-size: 0.8125rem;
  font-family: inherit;
  color: #1e293b;
  background: #fff;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.cell-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.cell-input::-webkit-inner-spin-button,
.cell-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.cell-input { -moz-appearance: textfield; }

/* Petit écran : cartes */
.header-actions--compact { width: 100%; }
.header-actions--compact .v-select { flex: 1 1 auto; }
.add-actions--compact > .v-btn { flex: 1 1 0; min-height: 44px; }
.compact-search >>> input { font-size: 16px !important; }

.batch-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (min-width: 600px) {
  .batch-cards { grid-template-columns: 1fr 1fr; }
}

.batch-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.batch-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.batch-card-name { min-width: 0; font-size: 0.9375rem; overflow-wrap: anywhere; }
.batch-card-remove { flex-shrink: 0; margin-right: -8px; }
.batch-card-stock { margin: 8px 0 10px; font-size: 0.875rem; color: #475569; }
.batch-card-fields { display: grid; grid-template-columns: 1.3fr 1fr; gap: 10px; }
.batch-card-total { margin-top: 10px; text-align: right; font-size: 0.875rem; color: #475569; }
.batch-card-total strong { color: #1e293b; }

.touch-input {
  display: block;
  width: 100%;
  min-width: 0;
  height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  padding: 0 10px;
  font-size: 16px; /* 16 px : évite le zoom automatique d'iOS au focus */
  font-family: inherit;
  color: #1e293b;
  background: #fff;
  text-align: center;
  font-variant-numeric: tabular-nums;
  -moz-appearance: textfield;
}
.touch-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15); }
.touch-input::-webkit-inner-spin-button,
.touch-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }

.qty-stepper { display: flex; gap: 6px; }
.qty-stepper .touch-input { flex: 1 1 auto; }
.qty-btn {
  flex: 0 0 44px;
  height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #194173;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  touch-action: manipulation;
}
.qty-btn:active { background: #e2e8f0; }

.batch-bottom-bar {
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.08);
}
.batch-bottom-total { font-size: 1.125rem; font-weight: 700; color: #1e293b; font-variant-numeric: tabular-nums; }

/* Dialogs */
.dialog-card { border-radius: 12px !important; }
.dialog-title { font-size: 0.9375rem !important; font-weight: 600 !important; padding: 14px 20px 12px !important; }
.dialog-body { padding: 20px 20px 8px !important; }
.dialog-actions { padding: 8px 16px 12px !important; }

.form-group { margin-bottom: 16px; }
.form-label { display: block; font-size: 0.75rem; font-weight: 600; color: #475569; margin-bottom: 4px; }
.recap-value { font-size: 0.875rem; font-weight: 600; color: #1e293b; }

/* Danger zone */
.danger-zone { border: 1px dashed #F44336; border-radius: 8px; padding: 12px 16px; }
.danger-zone-title { color: #F44336; font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: .6px; margin-bottom: 8px; }
</style>
